import React, { useEffect, useMemo, useRef, useState } from "react";
import { getBase64, getBlankPdf } from "../../helpers/app";
import {
  markSuccessToFileUploaded,
  resetPdfFileAndUrl,
  resetPdfFileData,
  setIsCustomFile,
  setPdfFile,
  setPdfFileName,
} from "../../reducers/pdf-viewer/actions";
import { useDispatch, useSelector } from "react-redux";
import SplitPane from "react-split-pane";
import classes from "./simplified-pdf-viewer.module.scss";
import ResultTabs from "./atomic/result-tabs";
import { Popconfirm, Spin, Tabs, Tooltip } from "antd";
import PDFEditor from "./atomic/pdf-editor";
import { POINTER_MODES } from "../../constants/editor";
import {
  clearAllDetectedFields,
  getPDFFieldsInfo,
} from "../../reducers/pdf-detected-fields/actions";
import EditAddObjects from "./atomic/edit-add-objects";
import ObjectDetailsModal from "../../components/object-details-modal";
import {
  selectDraftingObject,
  selectJsonTemplateData,
  selectProjectObject, selectTogglePreview,
} from "../../reducers/project/selectors";
import { isEmpty, isNil } from "ramda";
import {
  addObjectsFromTemplate,
  addObjectToProject,
  clearAllObjectsProject,
  removeObjectProject,
  setDraftingObject,
  setJsonTemplateData,
  updateObjectProject,
} from "../../reducers/project/actions";
import { showErrorMessageFromServer } from "../../helpers/Request.helper";
import classNames from "classnames";
import {
  setActiveDetectedField,
  setActiveObjectId,
} from "../../reducers/editor/actions";
import { OBJECT_TYPES } from "../../constants/project";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faImage,
  faRectangleList,
  faSave,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import yamljs from "yamljs";
import { selectPdfDetectedFields } from "../../reducers/pdf-detected-fields/selectors";
import {
  selectCanvasHeight, selectCurrentPageOriginalHeight,
  selectFile,
  selectFileName,
  selectPage,
} from "../../reducers/pdf-viewer/selectors";
import { v4 as uuidv4 } from "uuid";
import { imageToPdf } from "../../helpers/imageToPdf";
import TabPane from "antd/es/tabs/TabPane";
import TemplateData from "./atomic/template-data";

const SimplifiedPDFViewer = () => {
  const dispatch = useDispatch();

  const projectObjects = useSelector(selectProjectObject);
  const detectedFields = useSelector(selectPdfDetectedFields);
  const fileName = useSelector(selectFileName);
  const jsonTemplateData = useSelector(selectJsonTemplateData);
  const fileData = useSelector(selectFile);
  const togglePreView = useSelector(selectTogglePreview);
  const canvasHeight = useSelector(selectCanvasHeight);
  const currentPageOriginalHeight = useSelector(
    selectCurrentPageOriginalHeight
  );

  const uploadRef = useRef(null);
  const uploadTemplateRef = useRef();

  const [isLoading, setIsLoading] = useState(false);
  const [currentLoadingMessage, setCurrentLoadingMessage] = useState("");

  const draftingObject = useSelector(selectDraftingObject);
  const currentPage = useSelector(selectPage);

  const handleHideObjectDetailModal = () => {
    dispatch(setDraftingObject(null));
  };

  const isHasNoProjectObjectsAndDetectedFields = useMemo(() => {
    return isEmpty(projectObjects) && isEmpty(detectedFields);
  }, [projectObjects, detectedFields]);

  const onUploadFile = () => {
    if (!isHasNoProjectObjectsAndDetectedFields) {
      dispatch(resetPdfFileData());
      dispatch(clearAllObjectsProject());
      dispatch(clearAllDetectedFields());
      dispatch(setPdfFile(getBlankPdf()));
    }

    if (uploadRef && uploadRef.current) {
      uploadRef.current.click();
    }
  };

  const onForceUploadFile = () => {
    if (uploadRef && uploadRef.current) {
      uploadRef.current.click();
    }
  };

  const getDetectedField = (data) => {
    setIsLoading(true);
    setCurrentLoadingMessage(
      <p style={{ fontSize: "24px", marginTop: "10px" }}>
        Loading information and fields info from the document
      </p>
    );
    dispatch(getPDFFieldsInfo(data))
      .then(() => {})
      .catch(showErrorMessageFromServer)
      .finally(() => {
        setIsLoading(false);
        setCurrentLoadingMessage("");
      });
  };

  const handleFileUploaded = async (e, files) => {
    if (files && files.length) {
      dispatch(resetPdfFileAndUrl());

      dispatch(setPdfFileName(files[0].name));

      const mimeType = files[0].type;

      if (mimeType === "application/pdf") {
        getBase64(files[0], (result) => {
          dispatch(setPdfFile(result));
          dispatch(setIsCustomFile(true));

          dispatch(markSuccessToFileUploaded());
        });
        getDetectedField({ file: files[0] });
      } else if (["image/jpeg", "image/png"].includes(mimeType)) {
        setIsLoading(true);
        const pdfData = await imageToPdf(files[0], mimeType);
        const blob = new Blob([pdfData], { type: "application/pdf" });

        getBase64(blob, (result) => {
          dispatch(setPdfFile(result));
          dispatch(setIsCustomFile(true));

          dispatch(markSuccessToFileUploaded());
        });
        getDetectedField({ file: blob });

        // Old version - need to send to backend
        // setCurrentLoadingMessage(
        //   <p style={{ fontSize: "24px", marginTop: "10px" }}>
        //     Converting file to PDF
        //   </p>
        // );
        // dispatch(
        //   convertToPdf({
        //     file: files[0],
        //   })
        // )
        //   .then((res) => {
        //     dispatch(setPdfUrl(res.payload.url));
        //     dispatch(setIsCustomFile(true));
        //
        //     getDetectedField({ url: res.payload.url });
        //
        //     dispatch(markSuccessToFileUploaded());
        //   })
        //   .catch(showErrorMessageFromServer)
        //   .finally(() => {
        //     setIsLoading(false);
        //     setCurrentLoadingMessage("");
        //   });
      }
    }
    e.target.value = "";
  };

  const onSaveProject = () => {
    const a = document.createElement("a");
    const file = new Blob(
      [
        JSON.stringify(
          {
            objects: projectObjects,
            templateData: JSON.parse(jsonTemplateData),
            file: fileData,
          },
          null,
          2
        ),
      ],
      {
        type: "application/json",
      }
    );
    a.href = URL.createObjectURL(file);
    a.download = `${fileName ? fileName : "empty"}-pdf-edit-add-helper.json`;
    a.click();
  };

  const onUploadTemplateClick = (e) => {
    if (!projectObjects.length) {
      uploadTemplateRef.current.click();
    } else if (
      window.confirm("Are you sure you want to discard all current objects?")
    ) {
      uploadTemplateRef.current.click();
    }
  };

  const handleTemplateUploaded = (e) => {
    const files = e.target.files;
    const fr = new FileReader();
    const extension = files[0].name.split(".").pop();

    fr.onload = function (e) {
      let result;
      if (extension === "json") {
        result = JSON.parse(e.target.result);
      } else if (extension === "yml") {
        result = yamljs(e.target.result);
      }

      setTemplate(result);
    };

    fr.readAsText(files.item(0));

    e.target.value = "";

    function setTemplate(template) {
      try {
        dispatch(addObjectsFromTemplate(template.objects));
        dispatch(
          setJsonTemplateData(JSON.stringify(template.templateData, null, 4))
        );
        dispatch(setPdfFile(template.file));

        // Call API to detected fields from template
        getDetectedField({ base64: template.file })

      } catch (e) {
        dispatch(addObjectsFromTemplate([]));
        dispatch(setJsonTemplateData(""));
        console.log(e, "Template error");
        alert("Template error");
      }
    }
  };

  const onSaveProjectObject = (data, isEditing) => {
    if (isEditing) {
      dispatch(updateObjectProject(data));
    } else {
      dispatch(addObjectToProject(data));
      if (data.objectType === OBJECT_TYPES.DETECTED_FIELD) {
        dispatch(setActiveDetectedField(data.id));
      } else {
        dispatch(setActiveObjectId(data.objectId));
      }
    }
    dispatch(setDraftingObject(null));
  };

  const onRemoveProjectObject = (id) => {
    dispatch(removeObjectProject(id));
    dispatch(setDraftingObject(null));
  };

  const onAddObjectByButton = (objectType) => {
    const defaultData =
      objectType === OBJECT_TYPES.ANNOTATION
        ? {
            x: 2,
            y: 2,
            width: 230,
            height: 18 * currentPageOriginalHeight / canvasHeight,
            pages: currentPage - 1,
            text: "Please replace this text with something",
            objectType: objectType,
            objectId: uuidv4(),
          }
        : {
            x: 2,
            y: 2,
            url: "https://pdfco-test-files.s3.us-west-2.amazonaws.com/pdf-edit/logo.png",
            pages: currentPage - 1,
            objectType: objectType,
            objectId: uuidv4(),
          };

    dispatch(addObjectToProject(defaultData));
    dispatch(setActiveObjectId(defaultData.objectId));
  };

  useEffect(() => {
    dispatch(setPdfFile(getBlankPdf()));
  }, []);

  useEffect(() => {
    if (togglePreView) {
      setIsLoading(true);
      setCurrentLoadingMessage(
        <p style={{ fontSize: "24px", marginTop: "10px" }}>
          Applying the changes into the PDF file
        </p>
      );
    } else {
      setIsLoading(false);
      setCurrentLoadingMessage("");
    }
  }, [togglePreView]);

  return (
    <Spin
      size={"large"}
      className={classes.Loading}
      spinning={isLoading}
      tip={currentLoadingMessage}
    >
      <div className={classes.ContentWrap}>
        <SplitPane
          className={classes.SplitPaneWrap}
          style={{ maxHeight: "100%" }}
          defaultSize={
            parseInt(
              localStorage.getItem("PDFcoDocumentParserEditorsplitPos"),
              10
            ) || "45%"
          }
          onChange={(size) =>
            localStorage.setItem("PDFcoDocumentParserEditorsplitPos", size)
          }
          maxSize={-400}
          minSize={100}
        >
          <SplitPane
            split="horizontal"
            className={classes.SplitPaneWrap}
            style={{ maxHeight: "100%" }}
            defaultSize="60%"
            onChange={() => {}}
            maxSize={-100}
            minSize={100}
          >
            <div style={{ width: "100%", display: "flex" }}>
              <div className={classes.ObjectListWrap}>
                <div>
                  <div className={classes.ActionWrap}>
                    <div className={classes.FileButtonWrap}>
                      {isHasNoProjectObjectsAndDetectedFields ? (
                        <button
                          className={classNames(
                            classes.Button,
                            "btn btn-primary"
                          )}
                          onClick={onUploadFile}
                        >
                          Load PDF, PDF form, JPG, PNG image...
                        </button>
                      ) : (
                        <Popconfirm
                          placement="topLeft"
                          title={"Do want to discard all current objects?"}
                          description={null}
                          onConfirm={onUploadFile}
                          onCancel={onForceUploadFile}
                          okText="Yes"
                          cancelText="No"
                        >
                          <button
                            className={classNames(
                              classes.Button,
                              "btn btn-primary"
                            )}
                          >
                            Load PDF, PDF form, JPG, PNG image...
                          </button>
                        </Popconfirm>
                      )}
                    </div>

                    <div className={classes.ProjectButtons}>
                      <button
                        className={classNames(
                          classes.Button,
                          "btn btn-default me-1"
                        )}
                        onClick={onUploadTemplateClick}
                      >
                        <FontAwesomeIcon icon={faUpload} />
                        <span className={"ms-2"}>Load Project..</span>
                      </button>
                      <button
                        className={classNames(
                          classes.Button,
                          "btn btn-default"
                        )}
                        onClick={onSaveProject}
                      >
                        <FontAwesomeIcon icon={faSave} />
                        <span className={"ms-2"}>Save Project</span>
                      </button>
                    </div>
                  </div>

                  <div
                    className={classNames(
                      classes.FileName,
                      "text-primary ms-1"
                    )}
                  >
                    <Tooltip title={fileName} placement="bottom">
                      <span>{fileName}</span>
                    </Tooltip>

                    <div className={classes.AddObjectWrap}>
                      <button
                        className={classNames(
                          classes.Button,
                          "btn btn-primary"
                        )}
                        onClick={() =>
                          onAddObjectByButton(OBJECT_TYPES.ANNOTATION)
                        }
                      >
                        <FontAwesomeIcon icon={faRectangleList} />
                        <span className={"ms-2"}>Add Text</span>
                      </button>

                      <button
                        className={classNames(
                          classes.Button,
                          "btn btn-primary ms-1 me-1"
                        )}
                        onClick={() => onAddObjectByButton(OBJECT_TYPES.IMAGE)}
                      >
                        <FontAwesomeIcon icon={faImage} />
                        <span className={"ms-2"}>Add Image</span>
                      </button>
                    </div>
                  </div>
                </div>

                <div style={{ flex: "1 0 1px", overflow: "hidden" }}>
                  <Tabs
                    type="card"
                    defaultActiveKey="zapier"
                    className={classNames(classes.TabsWrap, "mt-1")}
                  >
                    <TabPane
                      tab="Objects (text, images, pdf fields)"
                      key="objects"
                    >
                      <EditAddObjects onDelete={onRemoveProjectObject} />
                    </TabPane>
                    <TabPane tab="Sample Template Data" key="template">
                      <TemplateData />
                    </TabPane>
                  </Tabs>
                </div>
              </div>

              <div className={classes.Hidden}>
                <input
                  ref={(ref) => (uploadRef.current = ref)}
                  type="file"
                  className="form-control"
                  accept="application/pdf,image/jpeg,image/png"
                  id="uploadPdf"
                  onChange={(e) => handleFileUploaded(e, e.target.files)}
                />
              </div>
              <div className={classes.Hidden}>
                <input
                  ref={(ref) => (uploadTemplateRef.current = ref)}
                  type="file"
                  className="form-control"
                  accept=".json,.yml"
                  id="uploadTemplate"
                  onChange={(e) => handleTemplateUploaded(e)}
                />
              </div>
            </div>
            <ResultTabs />
          </SplitPane>

          <PDFEditor acceptedModes={[POINTER_MODES.POSITION]} />
        </SplitPane>
      </div>

      {!isNil(draftingObject) && (
        <ObjectDetailsModal
          object={draftingObject}
          isOpen={!isNil(draftingObject)}
          handleHide={handleHideObjectDetailModal}
          onSubmit={onSaveProjectObject}
          onDelete={onRemoveProjectObject}
        />
      )}
    </Spin>
  );
};

export default SimplifiedPDFViewer;
